import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  isDevMode,
} from '@angular/core';
import {
  NavigationError,
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withNavigationErrorHandler,
  withViewTransitions,
} from '@angular/router';
import {
  provideHttpClient,
  withInterceptors,
} from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideNativeDateAdapter } from '@angular/material/core';
import { provideServiceWorker } from '@angular/service-worker';
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideFirestore, getFirestore } from '@angular/fire/firestore';
import { provideAuth, getAuth } from '@angular/fire/auth';
import { PixelModule } from '@felipeclopes/ngx-pixel';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';

import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { encryptionInterceptor } from 'src/@order/services/encryptioninterceptor.service';
import { dataDecryptionInterceptor } from 'src/@order/services/data-decryption-intercaptor.service';
import { ensureZoneInterceptor } from 'src/@order/services/ensure-zone.interceptor';

// A failed lazy-chunk load happens when the deployed/rebuilt app produced new
// chunk hashes but an already-open tab still references the old ones (404).
// The current app instance can't recover in place — only a hard reload fetches
// a fresh index.html with the new hashes.
function isChunkLoadError(error: any): boolean {
  const message = String(error?.message ?? error ?? '');
  return (
    error?.name === 'ChunkLoadError' ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /Loading chunk \d+ failed/i.test(message)
  );
}

// Hard-reload to recover from a stale chunk, but only once within a short
// window so a genuinely missing chunk can't trap the user in a reload loop.
function reloadForChunkError(targetUrl?: string): void {
  const key = 'chunkReloadAt';
  const last = Number(sessionStorage.getItem(key) ?? 0);
  if (Date.now() - last < 10000) {
    console.error('Chunk failed to load again after reload; not retrying.');
    return;
  }
  sessionStorage.setItem(key, String(Date.now()));
  window.location.assign(targetUrl || window.location.href);
}

// Global error handler — recovers from stale lazy chunks, suppresses Google
// Maps noise, logs everything else.
class GlobalErrorHandler implements ErrorHandler {
  handleError(error: any): void {
    if (isChunkLoadError(error)) {
      console.warn('Lazy chunk failed to load; reloading to recover.', error);
      reloadForChunkError();
      return;
    }
    if (
      error?.message?.includes('vI') ||
      error?.message?.includes('Google Maps') ||
      error?.message?.includes('loading=async') ||
      error?.stack?.includes('maps.googleapis.com')
    ) {
      console.warn('Google Maps related error (non-critical):', error);
      return;
    }
    console.error('An error occurred:', error);
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes,
      withComponentInputBinding(),
      withViewTransitions(),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
      // Recover from stale lazy-route chunks (e.g. after a deploy) by reloading
      // the target URL so the browser fetches a fresh index.html + new chunks.
      withNavigationErrorHandler((event: NavigationError) => {
        if (isChunkLoadError(event?.error)) {
          console.warn(
            'Lazy route chunk failed to load; reloading to recover.',
            event.error,
          );
          reloadForChunkError(event?.url);
        }
      }),
    ),
    provideHttpClient(
      withInterceptors([
        ensureZoneInterceptor,
        encryptionInterceptor,
        dataDecryptionInterceptor,
      ]),
    ),
    provideAnimationsAsync(),
    provideNativeDateAdapter(),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode() && environment.serviceWorker,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    provideFirestore(() => getFirestore()),
    provideAuth(() => getAuth()),
    importProvidersFrom(
      PixelModule.forRoot({ enabled: true, pixelId: '3904784016434343' }),
    ),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    {
      provide: OVERLAY_DEFAULT_CONFIG,
      useValue: {
        usePopover: false,
      },
    },
  ],
};
